// src/features/calendar/components/EventForm.tsx
import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, MapPinIcon, DocumentTextIcon, CheckIcon, XMarkIcon, SparklesIcon, UserIcon, FolderIcon, CurrencyDollarIcon, DocumentDuplicateIcon, TagIcon } from '@heroicons/react/24/outline';
import { useCalendarStore } from '../store/calendarStore';
import { useCalendarApi } from '../hooks/useCalendarApi';
import { LexicalEventEditor } from './LexicalEventEditor';
import { EVENT_TYPES, getEventTypeById } from '../constants/eventTypes';
import type { CalendarEvent, EventFormData, EdgeCalendar } from '../types/calendar.types';

interface EventFormProps {
  isOpen: boolean;
  onClose: () => void;
  event?: CalendarEvent;
  initialStart?: Date;
  initialEnd?: Date;
  matterId?: string;
}

const EventForm: React.FC<EventFormProps> = ({
  isOpen,
  onClose,
  event,
  initialStart,
  initialEnd,
  matterId,
}) => {
  const { calendars, currentDate } = useCalendarStore();
  const { createEvent, updateEvent: updateEventApi, loading: apiLoading } = useCalendarApi();
  
  // Form step state
  const [step, setStep] = useState<'form' | 'calendars'>('form');
  const [savedEvent, setSavedEvent] = useState<CalendarEvent | null>(null);
  
  // Create default dates based on current calendar view
  const getDefaultStartDate = () => {
    if (event?.start) return event.start;
    if (initialStart) return initialStart;
    const defaultDate = new Date(currentDate);
    defaultDate.setHours(9, 0, 0, 0);
    return defaultDate;
  };

  const getDefaultEndDate = () => {
    if (event?.end) return event.end;
    if (initialEnd) return initialEnd;
    const startDate = getDefaultStartDate();
    const endDate = new Date(startDate);
    endDate.setHours(startDate.getHours() + 1);
    return endDate;
  };
  
  const [formData, setFormData] = useState<EventFormData>(() => ({
    title: event?.title || '',
    start: getDefaultStartDate(),
    end: getDefaultEndDate(),
    allDay: event?.allDay || false,
    description: event?.description || '',
    location: event?.location || '',
    calendarId: event?.calendarId || (calendars.length > 0 ? calendars[0].id : ''),
    matterId: event?.matterId || matterId || '',
    contactIds: event?.contactIds || [],
    attendees: [],
    priority: event?.priority || 'medium',
    eventTypeId: event?.eventTypeId || '',
    reminders: [],
  }));

  // State for Lexical editor content
  const [_descriptionJson, setDescriptionJson] = useState<string>('');
  const [showLexicalHelp, setShowLexicalHelp] = useState(false);

  const [additionalCalendars, setAdditionalCalendars] = useState<string[]>([]);

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate we have calendars loaded
    if (calendars.length === 0) {
      alert('No calendars available. Please wait for calendars to load or refresh the page.');
      return;
    }
    
    // Validate required fields
    if (!formData.title.trim()) {
      alert('Please enter an event title.');
      return;
    }
    
    if (!formData.calendarId) {
      alert('Please select a calendar.');
      return;
    }
    
    console.log('🔍 [EventForm] Submitting with data:', formData);
    console.log('🔍 [EventForm] Available calendars:', calendars);
    
    try {
      if (event) {
        // Editing existing event - use API
        await updateEventApi(event.id, formData);
        onClose();
      } else {
        // Creating new event - use API
        if (matterId && calendars.length > 1) {
          // Save form data for calendar selection step
          const eventData = {
            ...formData,
            matterId,
            // Add required CalendarEvent properties for the saved state
            id: `temp-${Date.now()}`,
            createdBy: 'current-user',
            createdAt: new Date(),
            updatedAt: new Date(),
            isDeleted: false,
            attendees: []
          };
          setSavedEvent(eventData);
          setStep('calendars');
        } else {
          // Just create the event and close
          console.log('🔄 [EventForm] Calling createEvent with:', formData);
          await createEvent(formData);
          console.log('✅ [EventForm] Event created successfully');
          onClose();
        }
      }
    } catch (error) {
      console.error('💥 [EventForm] Failed to save event:', error);
      // Show user-friendly error message
      alert(`Failed to save event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleFinalSave = async () => {
    if (!savedEvent) return;
    
    try {
      // Extract clean EventFormData from savedEvent (remove frontend-only properties)
      const cleanEventData: EventFormData = {
        title: savedEvent.title,
        start: savedEvent.start,
        end: savedEvent.end,
        allDay: savedEvent.allDay,
        description: savedEvent.description || '',
        location: savedEvent.location || '',
        calendarId: savedEvent.calendarId,
        contactIds: [],
        attendees: [],
        priority: savedEvent.priority || 'medium',
        eventTypeId: savedEvent.eventTypeId,
        reminders: [],
        matterId: savedEvent.matterId
      };

      // Create event in primary calendar using API
      await createEvent(cleanEventData);
      
      // Create events in additional calendars
      for (const calendarId of additionalCalendars) {
        const additionalEventData = {
          ...cleanEventData,
          calendarId: calendarId,
        };
        await createEvent(additionalEventData);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save events:', error);
      // Error handling is done by the API hook
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'datetime-local') {
      setFormData(prev => ({ ...prev, [name]: new Date(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAdditionalCalendarChange = (calendarId: string, checked: boolean) => {
    setAdditionalCalendars(prev => 
      checked 
        ? [...prev, calendarId]
        : prev.filter(id => id !== calendarId)
    );
  };

  const formatDateTime = (date: Date) => {
    return date.toISOString().slice(0, -8);
  };

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
    }
  };

  const selectedCalendar = calendars.find(cal => cal.id === formData.calendarId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[95vh] flex flex-col">
        
        {step === 'form' ? (
          <>
            {/* Enhanced Header */}
            <div className="bg-gradient-to-r from-lex-accent to-lex-accent-hover px-6 py-4 text-lex-accent-contrast flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <CalendarIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {event ? 'Edit Event' : 'Create New Event'}
                    </h2>
                    <p className="text-lex-accent-contrast/80 text-sm">
                      Use @ # $ /doc to link contacts, matters, bills, and documents
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  
                  {/* Left Column - Basic Details */}
                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <DocumentTextIcon className="h-5 w-5" />
                        Event Details
                      </h3>
                      
                      {/* Title */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          Event Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent text-lg font-medium placeholder-gray-400"
                          placeholder="Enter event title..."
                        />
                      </div>

                      {/* Date and Time */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                            <ClockIcon className="h-4 w-4" />
                            Start Time
                          </label>
                          <input
                            type="datetime-local"
                            name="start"
                            value={formatDateTime(formData.start)}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            End Time
                          </label>
                          <input
                            type="datetime-local"
                            name="end"
                            value={formatDateTime(formData.end)}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* All Day Toggle */}
                      <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-4">
                        <input
                          type="checkbox"
                          name="allDay"
                          checked={formData.allDay}
                          onChange={handleInputChange}
                          className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                        />
                        <label className="text-sm font-medium text-gray-900 dark:text-white">
                          All day event
                        </label>
                      </div>

                      {/* Location */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4" />
                          Location
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                          placeholder="Add location..."
                        />
                      </div>

                      {/* Calendar and Priority */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Calendar
                          </label>
                          <select
                            name="calendarId"
                            value={formData.calendarId}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                          >
                            {calendars.map((calendar: EdgeCalendar) => (
                              <option key={calendar.id} value={calendar.id}>
                                {calendar.name}
                              </option>
                            ))}
                          </select>
                          {selectedCalendar && (
                            <div className="flex items-center gap-2 mt-1">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: selectedCalendar.color }}
                              />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {selectedCalendar.name}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Priority
                          </label>
                          <select
                            name="priority"
                            value={formData.priority}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                          <div className={`mt-1 px-2 py-1 rounded text-xs font-medium border inline-block ${getPriorityColor(formData.priority)}`}>
                            {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                          </div>
                        </div>
                      </div>

                      {/* Event Type */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <TagIcon className="h-4 w-4" />
                          Event Type
                        </label>
                        <select
                          name="eventTypeId"
                          value={formData.eventTypeId}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        >
                          <option value="">Select event type...</option>
                          {EVENT_TYPES.map((eventType) => (
                              <option key={eventType.id} value={eventType.id}>
                                {eventType.label}
                              </option>
                          ))}
                        </select>
                        {formData.eventTypeId && (
                          <div className="flex items-center gap-2 mt-1">
                            {(() => {
                              const selectedEventType = getEventTypeById(formData.eventTypeId);
                              if (selectedEventType) {
                                const IconComponent = selectedEventType.icon;
                                return (
                                  <>
                                    <IconComponent className="w-3 h-3 text-gray-500" />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {selectedEventType.label}
                                    </span>
                                  </>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Matter Link Info */}
                    {matterId && (
                      <div className="bg-lex-accent-soft border border-lex-accent rounded-xl p-4">
                        <div className="flex items-center gap-3 text-lex-accent">
                          <CheckIcon className="h-5 w-5" />
                          <div>
                            <div className="font-medium">Linked to Current Matter</div>
                            <div className="text-sm text-lex-accent">
                              This event will be automatically associated with the current matter
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Smart Description */}
                  <div className="space-y-6">
                    <div className="bg-lex-accent-soft rounded-xl p-6 border border-lex-accent">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <SparklesIcon className="h-5 w-5 text-lex-accent" />
                          Smart Description
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowLexicalHelp(!showLexicalHelp)}
                          className="px-3 py-1 text-xs bg-lex-accent-soft text-lex-accent rounded-full hover:bg-lex-state-hover transition-colors"
                        >
                          {showLexicalHelp ? 'Hide Help' : 'Help'}
                        </button>
                      </div>

                      {/* Compact Lexical Help Panel */}
                      {showLexicalHelp && (
                        <div className="mb-4 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-lex-accent">
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">@</span>
                              <span className="text-gray-600 dark:text-gray-400">Contacts</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">#</span>
                              <span className="text-gray-600 dark:text-gray-400">Matters</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">$</span>
                              <span className="text-gray-600 dark:text-gray-400">Bills</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">/doc</span>
                              <span className="text-gray-600 dark:text-gray-400">Docs</span>
                            </div>
                          </div>
                          <div className="mt-2 p-2 bg-lex-accent-soft rounded text-xs text-lex-accent">
                            <strong>Example:</strong> Meeting with @John-Smith about #Smith-v-Jones case
                          </div>
                        </div>
                      )}

                      {/* Streamlined Lexical Editor */}
                      <div className="relative">
                        <div className="border-2 border-dashed border-lex-accent rounded-lg overflow-hidden">
                          <LexicalEventEditor
                            placeholder="Enter event description... 

Type @ for contacts, # for matters, $ for bills, /doc for documents"
                            onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                            onJsonChange={setDescriptionJson}
                            className="min-h-[120px] p-3"
                          />
                        </div>
                        
                        {/* Compact Smart Features Indicator */}
                        <div className="absolute -top-2 right-3 bg-gradient-to-r from-lex-accent to-purple-600 text-lex-accent-contrast px-2 py-0.5 rounded-full text-xs font-medium shadow-lg">
                          <SparklesIcon className="h-3 w-3 inline mr-1" />
                          AI
                        </div>
                      </div>

                      {/* Compact Smart Features Summary */}
                      <div className="mt-3 flex justify-center gap-4">
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <UserIcon className="h-3 w-3 text-lex-accent" />
                          <span>Contacts</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <FolderIcon className="h-3 w-3 text-green-600" />
                          <span>Matters</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <CurrencyDollarIcon className="h-3 w-3 text-yellow-600" />
                          <span>Bills</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <DocumentDuplicateIcon className="h-3 w-3 text-purple-600" />
                          <span>Documents</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-8 border-t border-gray-200 dark:border-gray-700 mt-8">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={apiLoading}
                    className="flex-2 px-8 py-3 bg-gradient-to-r from-lex-accent to-lex-accent-hover hover:from-lex-accent-hover hover:to-lex-accent-hover text-lex-accent-contrast rounded-lg font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {apiLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {event ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-5 w-5" />
                        {event ? 'Update Event' : 'Create Event'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          /* Calendar Selection Step - Enhanced */
          <>
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 text-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <CheckIcon className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Event Created Successfully!</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Event Summary */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 mb-8 border border-green-200 dark:border-green-800">
                <h3 className="font-bold text-gray-900 dark:text-white text-xl mb-3">
                  {savedEvent?.title}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <div className="flex items-center gap-3">
                    <ClockIcon className="h-5 w-5" />
                    <span className="font-medium">
                      {savedEvent && formatDisplayDate(savedEvent.start)} - {savedEvent && formatDisplayDate(savedEvent.end)}
                    </span>
                  </div>
                  {savedEvent?.location && (
                    <div className="flex items-center gap-3">
                      <MapPinIcon className="h-5 w-5" />
                      <span>{savedEvent.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Calendars */}
              <div className="mb-8">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4 text-lg">
                  Add this event to additional calendars:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {calendars
                    .filter(calendar => calendar.id !== formData.calendarId)
                    .map((calendar) => (
                      <label key={calendar.id} className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={additionalCalendars.includes(calendar.id)}
                          onChange={(e) => handleAdditionalCalendarChange(calendar.id, e.target.checked)}
                          className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                        />
                        <div 
                          className="w-5 h-5 rounded-full"
                          style={{ backgroundColor: calendar.color }}
                        />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {calendar.name}
                        </span>
                      </label>
                    ))}
                </div>
                
                {additionalCalendars.length > 0 && (
                  <div className="mt-4 p-4 bg-lex-accent-soft border border-lex-accent rounded-xl">
                    <p className="text-sm text-lex-accent">
                      <CheckIcon className="h-4 w-4 inline mr-1" />
                      This event will be created in <strong>{additionalCalendars.length + 1} calendars</strong>.
                    </p>
                  </div>
                )}
              </div>

              {/* Final Actions */}
              <div className="flex gap-4">
                <button
                  onClick={async () => {
                    if (savedEvent) {
                      try {
                        // Extract clean EventFormData from savedEvent
                        const cleanEventData: EventFormData = {
                          title: savedEvent.title,
                          start: savedEvent.start,
                          end: savedEvent.end,
                          allDay: savedEvent.allDay,
                          description: savedEvent.description || '',
                          location: savedEvent.location || '',
                          calendarId: savedEvent.calendarId,
                          contactIds: [],
                          attendees: [],
                          priority: savedEvent.priority || 'medium',
                          reminders: [],
                          matterId: savedEvent.matterId
                        };
                        await createEvent(cleanEventData);
                      } catch (error) {
                        console.error('Failed to create event:', error);
                      }
                    }
                    onClose();
                  }}
                  className="flex-1 px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                >
                  Skip & Finish
                </button>
                <button
                  onClick={handleFinalSave}
                  className="flex-2 px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <CheckIcon className="h-5 w-5" />
                  Add to All Selected Calendars
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventForm;
